import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ProductCard } from '../components/custom/ProductCard';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import api from '../utils/api';

function ProductListingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const category = searchParams.get('category') || 'all';
  const age = searchParams.get('age') || '';

  // SEO titles based on category
  const seoTitles = {
    'all': 'All Kids Lunchboxes & School Bags | Tiffin and Bag',
    'lunchbox': 'Kids Lunchboxes | Leak-Proof Tiffin Boxes | Tiffin and Bag',
    'bag': 'Kids School Bags | Backpacks for Children | Tiffin and Bag'
  };
  
  const seoDescriptions = {
    'all': 'Shop colorful lunchboxes and cool school bags for kids. BPA-free, leak-proof designs. Free delivery across India!',
    'lunchbox': 'Buy leak-proof kids lunchboxes online. Insulated tiffin boxes that keep food warm. Multiple compartments. Free delivery!',
    'bag': 'Shop comfortable school bags for kids. Padded straps, water bottle pockets. Fun designs kids love. Free delivery!'
  };

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const data = await api.getProducts(category === 'all' ? null : category);
        setProducts(data.products || []);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category]);

  const handleCategoryChange = (value) => {
    if (value === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category: value });
    }
  };

  // Get SEO values as plain strings
  const pageTitle = seoTitles[category] || seoTitles['all'];
  const pageDescription = seoDescriptions[category] || seoDescriptions['all'];
  const canonicalUrl = `https://tiffinandbag.com/products${category !== 'all' ? `?category=${category}` : ''}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      
    <div className="min-h-screen py-8 px-4" data-testid="product-listing-page">
      <div className="container mx-auto max-w-6xl">
        <h1 className="text-3xl font-display font-normal mb-6">Shop All Products</h1>

        {/* Category Tabs */}
        <Tabs value={category} onValueChange={handleCategoryChange} className="mb-8">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="lunchbox">🍱 Lunchboxes</TabsTrigger>
            <TabsTrigger value="bag">🎒 Bags</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-80 rounded-xl" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-lg text-muted-foreground">No products found</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {products.map((product, idx) => (
              <ProductCard key={product.id} product={product} showBestseller={idx < 2} />
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
}

export default ProductListingPage;